import React, { useMemo } from 'react'

import { DEFAULT_COLORS } from '@toasttab/sites-components'
import classnames from 'classnames'

import {
  DoMenus_GetMenuItemsItem as DoMenusGetMenuItemsItem,
  Maybe
} from 'src/apollo/onlineOrdering'
import { formatImageURL } from 'src/lib/js/utilities'
import { MenuItemTag } from 'src/public/components/default_template/online_ordering/item_tags/MenuItemTags'

import Image from 'shared/components/common/Image'
import { getItemImage } from 'shared/components/common/dynamic_menus/DynamicMenusUtils'
import { GroupItemBackupContent } from './types'
import PriceFromRange from 'shared/components/common/price/PriceFromRange'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import { getMenuItemSrcSetInProd } from 'public/components/default_template/menu_section/MenuItemCard'

import { GroupedMenuItemsProps } from './GroupedMenuItems'
import { t } from 'src/translations'

type SideImageItemProps = {
  item: DoMenusGetMenuItemsItem | GroupItemBackupContent
} & GroupedMenuItemsProps

// A menu item that is designed to live inside a LinkedItem inside of a MenuGroup block.
// This item is layed out with the image is to the side of the text.
export const SideImageItem = (props: SideImageItemProps) => {
  const { item, config } = props
  const {
    restaurant: { theme, meta }
  } = useRestaurant()

  const showTags = useMemo(
    () => config?.appearance?.showItemTags && Boolean(item.itemTags?.length),
    [config?.appearance.showItemTags, item.itemTags?.length]
  )
  const showDescription = useMemo(
    () => config?.appearance?.showDescription !== false && !!item.description,
    [config?.appearance?.showDescription, item.description]
  )

  const alignmentClass = useMemo(() => {
    if (config?.layout === 'textOnly') {
      return 'textOnly'
    }
    // Default to SideImage Layout.
    // When the image is on the left, the description alignment is on the right.
    if (config?.appearance?.imagePlacement === 'left') {
      return showDescription ? 'rightAligned' : 'rightAlignedNoDescr'
    } else {
      return showDescription ? 'leftAligned' : 'leftAlignedNoDescr'
    }
  }, [config?.layout, config?.appearance?.imagePlacement, showDescription])

  // Single price value (may be null for size-based pricing items)
  const price: string | Maybe<number> | undefined =
    'price' in item ? item.price : undefined
  // Array of prices for size-based pricing (e.g., small/medium/large)
  const prices: Array<{ price?: number | null }> | undefined =
    'prices' in item ? item.prices : undefined

  const themeEnabled = theme?.enabled
  const tagColor = themeEnabled
    ? theme?.colorScheme.border.default
    : meta.primaryColor || DEFAULT_COLORS.primary
  const textColor = themeEnabled
    ? theme?.colorScheme.text.default
    : meta.primaryTextColor || DEFAULT_COLORS.primaryText
  const imgSrcSet = useMemo(() => {
    if ('imageUrls' in item) {
      return getMenuItemSrcSetInProd(item.imageUrls)
    }
    return undefined
  }, [item])

  return (
    <div
      className={classnames('itemWrapper', alignmentClass, {
        priceHidden: config?.appearance?.showPrice === false
      })}
      key={item.guid}
    >
      <div className='itemName'>{item.name || t('menu-item.menu-item')}</div>
      <div className='itemDescription'>
        {showDescription ? item.description : ''}
      </div>
      <div className='itemPrice'>
        {config?.appearance?.showPrice !== false ? (
          <PriceFromRange price={price} prices={prices} />
        ) : (
          ''
        )}
      </div>
      <div className='itemImage'>
        <Image
          alt={''}
          src={formatImageURL(getItemImage(item))}
          srcSet={imgSrcSet}
          sizes='116px'
          className={'itemImageWrapper'}
        />
      </div>
      {showTags && (
        <div className='itemTags'>
          {item.itemTags?.map(({ name }) => (
            <MenuItemTag
              tagColor={tagColor}
              textColor={textColor}
              subtle
              key={name}
            >
              {name}
            </MenuItemTag>
          ))}
        </div>
      )}
      <div className='itemDelimiter'></div>
    </div>
  )
}

export default SideImageItem
